/**
 * Global validation rules aligned with backend (Joi + Mongoose) where applicable.
 * Course text: only full description + short description have character caps client-side.
 */

import { normalizeDateRangeRows, validateDateRanges } from '../utils/courseSchedule'
import type { CourseDateRangeRow } from '../utils/courseSchedule'

// ─── Patterns ─────────────────────────────────────────────────────────────
export const PATTERNS = {
  email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  phone: /^\+?[\d\s-()]+$/,
  slug: /^[a-z0-9-]+$/,
  website: /^https?:\/\/.+/,
  time24: /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/,
  password: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
} as const

// ─── Limits (aligned with backend) ────────────────────────────────────────
export const LIMITS = {
  // Auth
  firstName: { min: 2, max: 50 },
  lastName: { min: 2, max: 50 },
  password: { min: 8 },

  // School
  schoolName: { min: 2, max: 100 },
  slug: { min: 2, max: 100 },
  description: { min: 10, max: 700 },
  shortDescription: { min: 10, max: 200 },
  location: { min: 2, max: 100 },
  country: { min: 2, max: 50 },
  city: { min: 2, max: 50 },
  address: { max: 200 },
  featureName: { min: 2, max: 50 },
  featureIcon: { max: 10 },
  certName: { min: 2, max: 100 },
  certDescription: { max: 200 },
  certIssuer: { min: 2, max: 50 },
  certRegNumber: { max: 50 },
  imageAlt: { max: 100 },

  // Course — client enforces character limits only on full + short description
  courseDescription: { min: 10, max: 700 },
  courseShortDescription: { max: 200 },
  startDay: { min: 1, max: 31 },
  maxImages: 5,
  maxVideos: 2,

  // Room
  roomName: { max: 100 },
  roomCapacity: { min: 1, max: 20 },
  roomNotes: { max: 500 },

  // Message
  messageContent: { min: 1, max: 5000 },

  // Booking
  bookingName: { min: 2, max: 100 },
  bookingMessage: { max: 1000 },
  bookingNotes: { max: 1000 },
} as const

export type ValidationErrors = Record<string, string>

// ─── Auth ─────────────────────────────────────────────────────────────────
export function validateRegister(data: {
  firstName: string
  lastName: string
  email: string
  password: string
  confirmPassword: string
  phone?: string
}): ValidationErrors {
  const err: ValidationErrors = {}

  const r1 = (data.firstName ?? '').trim()
  if (!r1) err.firstName = 'First name is required'
  else if (r1.length < LIMITS.firstName.min)
    err.firstName = `First name must be at least ${LIMITS.firstName.min} characters`
  else if (r1.length > LIMITS.firstName.max)
    err.firstName = `First name cannot exceed ${LIMITS.firstName.max} characters`

  const r2 = (data.lastName ?? '').trim()
  if (!r2) err.lastName = 'Last name is required'
  else if (r2.length < LIMITS.lastName.min)
    err.lastName = `Last name must be at least ${LIMITS.lastName.min} characters`
  else if (r2.length > LIMITS.lastName.max)
    err.lastName = `Last name cannot exceed ${LIMITS.lastName.max} characters`

  const r3 = (data.email ?? '').trim()
  if (!r3) err.email = 'Email is required'
  else if (!PATTERNS.email.test(data.email)) err.email = 'Please enter a valid email address'

  const r4 = data.password ?? ''
  if (!r4) err.password = 'Password is required'
  else if (r4.length < LIMITS.password.min)
    err.password = `Password must be at least ${LIMITS.password.min} characters`
  else if (!PATTERNS.password.test(r4))
    err.password = 'Password must contain uppercase, lowercase, number, and special character (@$!%*?&)'

  const r5 = (data.confirmPassword ?? '').trim()
  if (!r5) err.confirmPassword = 'Please confirm your password'
  else if (data.password !== data.confirmPassword) err.confirmPassword = 'Passwords do not match'

  if (data.phone?.trim() && !PATTERNS.phone.test(data.phone))
    err.phone = 'Please enter a valid phone number'

  return err
}

export function validatePassword(password: string): string | null {
  if (!password) return 'Password is required'
  if (password.length < LIMITS.password.min)
    return `Password must be at least ${LIMITS.password.min} characters`
  if (!PATTERNS.password.test(password))
    return 'Password must contain uppercase, lowercase, number, and special character (@$!%*?&)'
  return null
}

export function validateUpdateProfile(data: {
  firstName?: string
  lastName?: string
  phone?: string
}): ValidationErrors {
  const err: ValidationErrors = {}
  if (data.firstName !== undefined && data.firstName.trim()) {
    if (data.firstName.trim().length < LIMITS.firstName.min)
      err.firstName = `First name must be at least ${LIMITS.firstName.min} characters`
    else if (data.firstName.trim().length > LIMITS.firstName.max)
      err.firstName = `First name cannot exceed ${LIMITS.firstName.max} characters`
  }
  if (data.lastName !== undefined && data.lastName.trim()) {
    if (data.lastName.trim().length < LIMITS.lastName.min)
      err.lastName = `Last name must be at least ${LIMITS.lastName.min} characters`
    else if (data.lastName.trim().length > LIMITS.lastName.max)
      err.lastName = `Last name cannot exceed ${LIMITS.lastName.max} characters`
  }
  if (data.phone?.trim() && !PATTERNS.phone.test(data.phone))
    err.phone = 'Please enter a valid phone number'
  return err
}

// ─── School ───────────────────────────────────────────────────────────────
export function validateSchool(data: {
  name: string
  slug: string
  description: string
  shortDescription?: string
  location: string
  country: string
  city: string
  address?: string
  phone?: string
  email?: string
  website?: string
}): ValidationErrors {
  const err: ValidationErrors = {}

  const n = (data.name ?? '').trim()
  if (!n) err.name = 'School name is required'
  else if (n.length < LIMITS.schoolName.min)
    err.name = `School name must be at least ${LIMITS.schoolName.min} characters`
  else if (n.length > LIMITS.schoolName.max)
    err.name = `School name cannot exceed ${LIMITS.schoolName.max} characters`

  const s = (data.slug ?? '').trim()
  if (!s) err.slug = 'Slug is required'
  else if (s.length < LIMITS.slug.min)
    err.slug = `Slug must be at least ${LIMITS.slug.min} characters`
  else if (s.length > LIMITS.slug.max)
    err.slug = `Slug cannot exceed ${LIMITS.slug.max} characters`
  else if (!PATTERNS.slug.test(s))
    err.slug = 'Slug can only contain lowercase letters, numbers, and hyphens'

  const d = (data.description ?? '').trim()
  if (!d) err.description = 'Description is required'
  else if (d.length < LIMITS.description.min)
    err.description = `Description must be at least ${LIMITS.description.min} characters`
  else if (d.length > LIMITS.description.max)
    err.description = `Description cannot exceed ${LIMITS.description.max} characters`

  if (data.shortDescription?.trim()) {
    if (data.shortDescription.trim().length < LIMITS.shortDescription.min)
      err.shortDescription = `Short description must be at least ${LIMITS.shortDescription.min} characters if provided`
    else if (data.shortDescription.trim().length > LIMITS.shortDescription.max)
      err.shortDescription = `Short description cannot exceed ${LIMITS.shortDescription.max} characters`
  }

  const loc = (data.location ?? '').trim() || (data.city ?? '').trim()
  if (!loc) err.location = 'Area/Locality or City is required'
  else if (loc.length < LIMITS.location.min)
    err.location = `Area/Locality must be at least ${LIMITS.location.min} characters`
  else if (loc.length > LIMITS.location.max)
    err.location = `Area/Locality cannot exceed ${LIMITS.location.max} characters`

  const ctry = (data.country ?? '').trim()
  if (!ctry) err.country = 'Country is required'
  else if (ctry.length < LIMITS.country.min)
    err.country = `Country must be at least ${LIMITS.country.min} characters`
  else if (ctry.length > LIMITS.country.max)
    err.country = `Country cannot exceed ${LIMITS.country.max} characters`

  const city = (data.city ?? '').trim()
  if (!city) err.city = 'City is required'
  else if (city.length < LIMITS.city.min)
    err.city = `City must be at least ${LIMITS.city.min} characters`
  else if (city.length > LIMITS.city.max)
    err.city = `City cannot exceed ${LIMITS.city.max} characters`

  if (data.address?.trim() && data.address.trim().length > LIMITS.address.max)
    err.address = `Address cannot exceed ${LIMITS.address.max} characters`

  if (data.email?.trim() && !PATTERNS.email.test(data.email))
    err.email = 'Please enter a valid email address'

  if (data.website?.trim() && !PATTERNS.website.test(data.website))
    err.website = 'Please enter a valid website URL (must start with http:// or https://)'

  if (data.phone?.trim() && !PATTERNS.phone.test(data.phone))
    err.phone = 'Please enter a valid phone number'

  return err
}

// ─── Room ─────────────────────────────────────────────────────────────────
export function validateRoom(data: {
  name: string
  type: string
  capacity: number
  notes?: string
}): ValidationErrors {
  const err: ValidationErrors = {}

  const n = (data.name ?? '').trim()
  if (!n) err.name = 'Room name is required'
  else if (n.length > LIMITS.roomName.max)
    err.name = `Room name cannot exceed ${LIMITS.roomName.max} characters`

  if (!(data.type ?? '').trim()) err.type = 'Room type is required'

  if (data.capacity == null || isNaN(data.capacity))
    err.capacity = 'Room capacity is required'
  else if (data.capacity < LIMITS.roomCapacity.min)
    err.capacity = `Room capacity must be at least ${LIMITS.roomCapacity.min}`
  else if (data.capacity > LIMITS.roomCapacity.max)
    err.capacity = `Room capacity cannot exceed ${LIMITS.roomCapacity.max}`

  if (data.notes?.trim() && data.notes.trim().length > LIMITS.roomNotes.max)
    err.notes = `Notes cannot exceed ${LIMITS.roomNotes.max} characters`

  return err
}

// ─── Course ───────────────────────────────────────────────────────────────
export interface CourseFormData {
  name: string
  description: string
  shortDescription?: string
  duration?: number
  startDay?: number
  dateRangeRows?: CourseDateRangeRow[]
  schedule?: { timeSlots?: Array<{ label?: string; startTime?: string; endTime?: string }> }
  inclusions?: string[]
  requirements?: string[]
  whoIsThisFor?: string[]
  courseHighlights?: string[]
  whatYouWillGain?: string[]
  curriculum?: Array<{ title: string; items: string[] }>
  media?: { images?: string[]; videos?: string[] }
  instructor?: { name?: string; bio?: string; certifications?: string[] }
  accommodation?: { mealPlan?: { description?: string } }
}

function nonEmptyLines(values?: string[]): string[] {
  return (values ?? []).map((v) => v.trim()).filter(Boolean)
}

function validateCourseBasicInfo(data: CourseFormData): ValidationErrors {
  const err: ValidationErrors = {}

  const n = (data.name ?? '').trim()
  if (!n) err.name = 'Course name is required'

  const d = (data.description ?? '').trim()
  if (!d) err.description = 'Description is required'
  else if (d.length < LIMITS.courseDescription.min)
    err.description = `Description must be at least ${LIMITS.courseDescription.min} characters`
  else if (d.length > LIMITS.courseDescription.max)
    err.description = `Description cannot exceed ${LIMITS.courseDescription.max} characters`

  if (data.shortDescription?.trim() && data.shortDescription.trim().length > LIMITS.courseShortDescription.max)
    err.shortDescription = `Short description cannot exceed ${LIMITS.courseShortDescription.max} characters`

  if (data.duration == null || data.duration < 1 || isNaN(data.duration))
    err.duration = 'Duration must be at least 1 day'

  if (data.startDay != null && (data.startDay < LIMITS.startDay.min || data.startDay > LIMITS.startDay.max))
    err.startDay = `Start day must be between ${LIMITS.startDay.min} and ${LIMITS.startDay.max}`

  if (nonEmptyLines(data.whoIsThisFor).length === 0) {
    err.whoIsThisFor = 'Who is this for is required — add at least one line'
  }
  if (nonEmptyLines(data.courseHighlights).length === 0) {
    err.courseHighlights = 'Course highlights are required — add at least one line'
  }
  if (nonEmptyLines(data.whatYouWillGain).length === 0) {
    err.whatYouWillGain = 'What you will gain is required — add at least one line'
  }

  const images = (data.media?.images || []).filter(
    (k) => k && !k.startsWith('blob:') && !k.startsWith('http://localhost'),
  )
  const videos = (data.media?.videos || []).filter(
    (k) => k && !k.startsWith('blob:') && !k.startsWith('http://localhost'),
  )
  if (images.length > LIMITS.maxImages) err.media = `Maximum ${LIMITS.maxImages} images allowed`
  if (videos.length > LIMITS.maxVideos) err.media = err.media || `Maximum ${LIMITS.maxVideos} videos allowed`

  return err
}

function validateCourseSchedule(data: CourseFormData): ValidationErrors {
  const err: ValidationErrors = {}

  const ranges = normalizeDateRangeRows(data.dateRangeRows || [])
  const scheduleErr = validateDateRanges(ranges)
  if (scheduleErr) err.dateRanges = scheduleErr

  const slots = data.schedule?.timeSlots || []
  if (slots.length === 0) err.schedule = 'At least one time slot is required'
  else {
    const slotWithoutLabel = slots.find((ts) => !(ts?.label || '').trim())
    if (slotWithoutLabel) err.schedule = 'Label is required for each time slot'
    for (let i = 0; i < slots.length; i++) {
      const ts = slots[i]
      if (ts?.startTime && !PATTERNS.time24.test(ts.startTime))
        err[`schedule.timeSlots[${i}].startTime`] = 'Start time must be in HH:MM format (24h)'
      if (ts?.endTime && !PATTERNS.time24.test(ts.endTime))
        err[`schedule.timeSlots[${i}].endTime`] = 'End time must be in HH:MM format (24h)'
    }
  }

  return err
}

/** Step 1 — basic info, description, media limits */
export function validateCourseStep1(data: CourseFormData): ValidationErrors {
  return validateCourseBasicInfo(data)
}

/** Step 2 — course date ranges and daily schedule */
export function validateCourseStep2(data: CourseFormData): ValidationErrors {
  return validateCourseSchedule(data)
}

export function validateCourse(data: CourseFormData): ValidationErrors {
  return {
    ...validateCourseBasicInfo(data),
    ...validateCourseSchedule(data),
  }
}

/** Wizard step (1–3) for the first validation error key */
export function getCourseValidationStep(errors: ValidationErrors): number {
  for (const key of Object.keys(errors)) {
    if (key === 'dateRanges' || key === 'schedule' || key.startsWith('schedule.')) {
      return 2
    }
  }
  return 1
}

// ─── Message ──────────────────────────────────────────────────────────────
export function validateMessage(content: string): ValidationErrors {
  const err: ValidationErrors = {}
  const v = (content ?? '').trim()
  if (!v) err.content = 'Message content is required'
  else if (v.length < LIMITS.messageContent.min) err.content = 'Message cannot be empty'
  else if (v.length > LIMITS.messageContent.max)
    err.content = `Message cannot exceed ${LIMITS.messageContent.max} characters`
  return err
}
